/**
 * Pick work order lines to invoice (INVOICE-BY-ITEM.md §6).
 * Route: /invoices/new/items?tc=<typingCenterId>&org=<internalOrgId>|walkIn=1&orgName=<name>[&invoice=<id>&invoiceNumber=<no>]
 * Lists the customer's completed / partly invoiced work orders with their lines not on an invoice yet. Without
 * `invoice` it creates a draft; with it, it adds the chosen lines to that draft.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Collapse, FormControl, IconButton, InputAdornment, InputLabel,
  MenuItem, Paper, Select, TextField, Typography,
} from '@mui/material';
import {
  ArrowBack, ExpandMore as ExpandIcon, ChevronRight as CollapseIcon, Search as SearchIcon,
} from '@mui/icons-material';
import ApiService from '../../services/api.service';
import { billableItemsService } from '../../services/invoices/billableItems.service';
import {
  initialSelection, toggleItem, toggleWorkOrder, workOrderCheckState, selectionSummary,
} from '../../components/invoices/billableSelection';

const FS = '0.8125rem';
const aed = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '');

const ItemRow = ({ item, checked, onToggle }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: { xs: 2, sm: 6 }, pr: 2, py: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
    <Checkbox size="small" checked={checked} onChange={onToggle} />
    <Typography sx={{ fontSize: FS, flex: 2, minWidth: 0 }} noWrap>{item.serviceName}</Typography>
    <Typography sx={{ fontSize: FS, flex: 2, minWidth: 0, color: 'text.secondary' }} noWrap>{item.customerName}</Typography>
    <Typography sx={{ fontSize: FS, width: 110, textAlign: 'right', flexShrink: 0 }}>{aed(item.lineTotal)}</Typography>
  </Box>
);

const PickWorkOrderItems = ({ currentUser }) => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const typingCenterId = params.get('tc') || currentUser?.typingCenterId || currentUser?.typing_center_id;
  const orgId = params.get('org');
  const walkIn = params.get('walkIn') === '1';
  const orgName = params.get('orgName') || (walkIn ? 'Walk-in' : 'Client');
  const invoiceId = params.get('invoice');
  const invoiceNumber = params.get('invoiceNumber');
  const backTo = invoiceId ? `/invoices/${invoiceId}` : '/invoices';

  const [workOrders, setWorkOrders] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [selection, setSelection] = useState(new Set());
  const [mappedOrgs, setMappedOrgs] = useState([]);
  const [billTo, setBillTo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Every listed line is billable, so a loaded work order starts with all its lines ticked.
  const load = useCallback(async (nextPage = 0) => {
    if (!walkIn && !orgId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await billableItemsService.listWorkOrders({
        typingCenterId, organizationId: orgId, walkIn, invoiceId, search, page: nextPage,
      });
      setWorkOrders((cur) => (nextPage ? [...cur, ...res.workOrders] : res.workOrders));
      setSelection((sel) => new Set([...(nextPage ? sel : []), ...initialSelection(res.workOrders)]));
      setPage(nextPage);
      setHasMore(res.hasMore);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || 'Could not load work orders.');
    } finally {
      setLoading(false);
    }
  }, [typingCenterId, orgId, walkIn, invoiceId, search]);

  useEffect(() => { const t = setTimeout(() => load(0), 300); return () => clearTimeout(t); }, [load]);

  useEffect(() => {
    if (!typingCenterId || invoiceId) return;
    ApiService.get(`/api/mappings?typingCenterId=${typingCenterId}`).then((res) => {
      const orgs = (res?.data?.data || []).map((m) => ({ id: m.organization_id, name: m.organization_name, code: m.organization_code || '' }));
      setMappedOrgs(orgs);
      if (orgs.length === 1) setBillTo(orgs[0].id);
    }).catch(() => setMappedOrgs([]));
  }, [typingCenterId, invoiceId]);

  const summary = selectionSummary(workOrders, selection);

  const save = async () => {
    setError(null);
    const lineIds = [...selection];
    setSaving(true);
    try {
      if (invoiceId) {
        await billableItemsService.addToInvoice(invoiceId, lineIds);
        navigate(backTo);
        return;
      }
      const mappedOrg = mappedOrgs.find((o) => o.id === billTo);
      if (!mappedOrg) { setError('Choose who the invoice is billed to.'); return; }
      const invoice = await billableItemsService.createFromItems({
        typingCenterId, organizationId: orgId, walkIn, mappedOrg, workOrderLineItemIds: lineIds,
      });
      if (invoice?.id) navigate(`/invoices/${invoice.id}`);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || 'Could not save the invoice.');
    } finally {
      setSaving(false);
    }
  };

  const renderWorkOrder = (wo) => {
    const state = workOrderCheckState(wo, selection);
    const open = !!expanded[wo.id];
    return (
      <Paper key={wo.id} variant="outlined" sx={{ mb: 1, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, flexWrap: 'wrap' }}>
          <IconButton size="small" onClick={() => setExpanded((e) => ({ ...e, [wo.id]: !open }))}>
            {open ? <ExpandIcon fontSize="small" /> : <CollapseIcon fontSize="small" />}
          </IconButton>
          <Checkbox size="small" checked={state === 'all'} indeterminate={state === 'some'}
            onChange={() => setSelection((sel) => toggleWorkOrder(sel, wo))} />
          <Typography sx={{ fontSize: FS, fontWeight: 600, color: 'primary.main' }}>{wo.number}</Typography>
          {wo.status === 'PARTIALLY_INVOICED' && (
            <Chip size="small" color="info" label="Partly invoiced" sx={{ fontSize: '0.6875rem', height: 20 }} />
          )}
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{fmtDate(wo.completedAt || wo.createdAt)}</Typography>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {`${wo.items.filter((i) => selection.has(i.id)).length} of ${wo.items.length} items`}
            {wo.invoicedItems > 0 ? ` · ${wo.invoicedItems} already invoiced` : ''}
          </Typography>
          <Typography sx={{ fontSize: FS, fontWeight: 600, width: 110, textAlign: 'right' }}>
            {aed(wo.items.reduce((s, i) => s + i.lineTotal, 0))}
          </Typography>
        </Box>
        <Collapse in={open} unmountOnExit>
          {wo.items.map((item) => (
            <ItemRow key={item.id} item={item} checked={selection.has(item.id)}
              onToggle={() => setSelection((sel) => toggleItem(sel, item))} />
          ))}
        </Collapse>
      </Paper>
    );
  };

  const toolbarSx = { '& .MuiInputBase-root': { height: 32 }, '& .MuiInputBase-input, & .MuiSelect-select': { fontSize: FS }, '& .MuiInputLabel-root': { fontSize: FS } };

  return (
    <Box sx={{ p: 0, width: 0, minWidth: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap', ...toolbarSx }}>
        <IconButton size="small" onClick={() => navigate(backTo)}><ArrowBack fontSize="small" /></IconButton>
        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>
          {invoiceId ? `Add to ${invoiceNumber || 'invoice'} · ${orgName}` : `New invoice · ${orgName}`}
        </Typography>
        <TextField size="small" placeholder="Search work orders..." value={search} onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: '1 1 200px', minWidth: 180, ml: 1 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment> }} />
        {!invoiceId && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Bill to</InputLabel>
            <Select label="Bill to" value={billTo} onChange={(e) => setBillTo(e.target.value)}>
              {mappedOrgs.map((o) => <MenuItem key={o.id} value={o.id} sx={{ fontSize: FS }}>{o.name}</MenuItem>)}
            </Select>
          </FormControl>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.75rem' }} onClose={() => setError(null)}>{error}</Alert>}

      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1 }}>
        Completed work orders with lines not invoiced yet. Untick lines to bill them later.
      </Typography>

      {loading && workOrders.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
      ) : workOrders.length === 0 ? (
        <Paper variant="outlined" sx={{ py: 4, textAlign: 'center', mb: 1 }}>
          <Typography sx={{ fontSize: FS, color: 'text.secondary' }}>No completed work orders to invoice for {orgName}.</Typography>
        </Paper>
      ) : workOrders.map(renderWorkOrder)}
      {hasMore && (
        <Button size="small" disabled={loading} onClick={() => load(page + 1)} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
          Load more
        </Button>
      )}

      <Paper variant="outlined" sx={{ position: 'sticky', bottom: 0, mt: 2, px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: FS, flex: 1 }}>
          <strong>{summary.count}</strong> item{summary.count === 1 ? '' : 's'} · <strong>{aed(summary.total)}</strong>
        </Typography>
        <Button onClick={() => navigate(backTo)} sx={{ textTransform: 'none', fontSize: FS }}>Cancel</Button>
        <Button variant="contained" disabled={saving || summary.count === 0} onClick={save} sx={{ textTransform: 'none', fontSize: FS }}>
          {saving ? 'Saving…' : invoiceId ? 'Add to invoice' : 'Create draft invoice'}
        </Button>
      </Paper>
    </Box>
  );
};

export default PickWorkOrderItems;
